import React, { useState, useEffect, useContext, useRef } from "react";
import { useHistory } from "react-router-dom";
import withWidth, { isWidthUp } from "@material-ui/core/withWidth";
import "emoji-mart/css/emoji-mart.css";
import { Picker, emojiIndex } from "emoji-mart";
import MicRecorder from "mic-recorder-to-mp3";
import clsx from "clsx";
import { isNil } from "lodash";
import { Reply } from "@material-ui/icons";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import InputBase from "@material-ui/core/InputBase";
import CircularProgress from "@material-ui/core/CircularProgress";
import { green } from "@material-ui/core/colors";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import IconButton from "@material-ui/core/IconButton";
import MoodIcon from "@material-ui/icons/Mood";
import SendIcon from "@material-ui/icons/Send";
import CancelIcon from "@material-ui/icons/Cancel";
import ClearIcon from "@material-ui/icons/Clear";
import WbIncandescentIcon from "@material-ui/icons/WbIncandescent";
import MicIcon from "@material-ui/icons/Mic";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import PauseIcon from "@material-ui/icons/Pause";
import StopIcon from "@material-ui/icons/Stop";
import { FormControlLabel, Switch, Menu, MenuItem, ListItemIcon, ListItemText, Tooltip, Fade, Typography } from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import InsertDriveFileOutlinedIcon from "@material-ui/icons/InsertDriveFileOutlined";
import ImageOutlinedIcon from "@material-ui/icons/ImageOutlined";
import HeadsetOutlinedIcon from "@material-ui/icons/HeadsetOutlined";
import DescriptionOutlinedIcon from "@material-ui/icons/DescriptionOutlined";
import { isString, isEmpty, isObject, has } from "lodash";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import axios from "axios";
import usePlans from "../../hooks/usePlans";

import RecordingTimer from "./RecordingTimer";
import { ReplyMessageContext } from "../../context/ReplyingMessage/ReplyingMessageContext";
import { ForwardMessageContext } from "../../context/ForwarMessage/ForwardMessageContext";
import { AuthContext } from "../../context/Auth/AuthContext";
import { useLocalStorage } from "../../hooks/useLocalStorage";
import toastError from "../../errors/toastError";
import toast from "../../utils/toast";

import Compressor from "compressorjs";
import ProgressBarCustom from "./ProgressBarCustom";
import useQuickMessages from "../../hooks/useQuickMessages";
import { emojiI18n, setupEmojiMartSearch } from "../../helpers/emojiPtBr";

// Setup bilingual Portuguese + English search for emoji-mart
setupEmojiMartSearch(emojiIndex);

const Mp3Recorder = new MicRecorder({ bitRate: 128 });

// ─── helpers ──────────────────────────────────────────────────────────────────

const getMediaIcon = (file) => {
  const type = file.type?.split("/")[0];
  if (type === "image") return <ImageOutlinedIcon style={{ fontSize: 28, color: "#007bfc" }} />;
  if (type === "audio") return <HeadsetOutlinedIcon style={{ fontSize: 28, color: "#ee6e00" }} />;
  return <DescriptionOutlinedIcon style={{ fontSize: 28, color: "#7f66ff" }} />;
};

const getMediaThumb = (file) => {
  if (file.type?.startsWith("image/")) return URL.createObjectURL(file);
  return null;
};

// ─── styles ───────────────────────────────────────────────────────────────────

const useStyles = makeStyles((theme) => {
  const dark = theme.palette.type === "dark";
  const bgBar     = dark ? "#202c33" : "#f0f2f5";
  const bgInput   = dark ? "#2a3942" : "#fff";
  const textMain  = dark ? "#e9edef" : "#111b21";
  const textMuted = dark ? "#8696a0" : "#54656f";

  return {
    // ── outer shell ──────────────────────────────────────────────────────────
    mainWrapper: {
      backgroundColor: bgBar,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      borderTop: `1px solid ${dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.08)"}`,
    },

    newMessageBox: {
      backgroundColor: bgBar,
      width: "100%",
      display: "flex",
      padding: "7px 10px",
      alignItems: "center",
      gap: 4,
      [theme.breakpoints.down("sm")]: { padding: "4px 6px" },
    },

    // ── text input wrapper ────────────────────────────────────────────────────
    messageInputWrapper: {
      flex: 1,
      minWidth: 0,
      display: "flex",
      alignItems: "center",
      backgroundColor: bgInput,
      borderRadius: 21,
      padding: "5px 14px",
      boxShadow: dark ? "none" : "0 1px 2px rgba(0,0,0,0.07)",
    },

    selectionBar: {
      flex: 1,
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      backgroundColor: dark ? "#202c33" : "#f0f2f5",
      padding: "8px 16px",
      borderRadius: 6,
    },

    messageInput: {
      flex: 1,
      fontSize: 15,
      lineHeight: "22px",
      color: textMain,
      "& textarea": {
        padding: 0,
      },
    },

    sendMessageIcons: {
      color: textMuted,
      transition: "color 0.15s",
      "&:hover": { color: textMain },
    },

    uploadInput: { display: "none" },

    // ── emoji picker (WhatsApp Web Style) ──────────────────────────────────────
    emojiBox: {
      position: "absolute",
      bottom: 54,
      left: 0,
      zIndex: 100,
      width: 440,
      maxWidth: "calc(100vw - 32px)",
      borderRadius: 16,
      overflow: "hidden",
      boxShadow: dark
        ? "0 10px 40px rgba(0,0,0,0.6), 0 0 1px rgba(255,255,255,0.12)"
        : "0 10px 32px rgba(0,0,0,0.18), 0 0 1px rgba(0,0,0,0.08)",
      border: `1px solid ${dark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"}`,
      backgroundColor: dark ? "#202c33" : "#ffffff",
      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",

      "& .emoji-mart": {
        border: "none !important",
        backgroundColor: "transparent !important",
        color: dark ? "#e9edef" : "#111b21",
        width: "100% !important",
        fontFamily: "inherit",
      },

      // Top category anchors bar
      "& .emoji-mart-bar": {
        border: "none !important",
        borderColor: "transparent !important",
        padding: "6px 12px 2px",
        backgroundColor: "transparent !important",
      },
      "& .emoji-mart-anchors": {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "0 4px",
      },
      "& .emoji-mart-anchor": {
        padding: "8px 4px 6px",
        color: dark ? "#8696a0" : "#54656f",
        transition: "color 0.15s ease",
        "&:hover": {
          color: dark ? "#e9edef" : "#111b21",
        },
      },
      "& .emoji-mart-anchor-selected": {
        color: "#00a884 !important",
      },
      "& .emoji-mart-anchor-bar": {
        backgroundColor: "#00a884 !important",
        height: "3px !important",
        borderRadius: "3px !important",
        bottom: "0 !important",
      },

      // Search bar
      "& .emoji-mart-search": {
        padding: "8px 14px 6px",
        position: "relative",
        marginTop: 0,
      },
      "& .emoji-mart-search input": {
        backgroundColor: dark ? "#111b21 !important" : "#f0f2f5 !important",
        color: dark ? "#e9edef !important" : "#111b21 !important",
        borderColor: "transparent !important",
        border: "none !important",
        borderRadius: "8px !important",
        height: "36px",
        padding: "6px 14px 6px 38px !important",
        fontSize: "13.5px",
        outline: "none !important",
        boxShadow: "none !important",
        "&::placeholder": {
          color: dark ? "#8696a0 !important" : "#8696a0 !important",
          opacity: 1,
        },
      },
      "& .emoji-mart-search-icon": {
        position: "absolute !important",
        left: "24px !important",
        right: "auto !important",
        top: "50% !important",
        transform: "translateY(-50%) !important",
        pointerEvents: "none",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        "& svg": {
          fill: dark ? "#8696a0 !important" : "#8696a0 !important",
          width: 16,
          height: 16,
        },
      },

      // Category labels
      "& .emoji-mart-category-label": {
        top: 0,
        zIndex: 2,
        padding: "4px 14px",
      },
      "& .emoji-mart-category-label span": {
        backgroundColor: `${dark ? "#202c33" : "#ffffff"} !important`,
        color: dark ? "#8696a0 !important" : "#667781 !important",
        fontSize: "13px",
        fontWeight: 500,
        padding: "4px 0",
      },

      // Scrollable grid & custom scrollbar
      "& .emoji-mart-scroll": {
        padding: "0 6px 8px 12px",
        height: "260px",
        overflowY: "auto",
        "&::-webkit-scrollbar": {
          width: "6px",
        },
        "&::-webkit-scrollbar-track": {
          background: "transparent",
        },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: dark ? "rgba(255,255,255,0.18)" : "rgba(0,0,0,0.18)",
          borderRadius: "6px",
        },
        "&::-webkit-scrollbar-thumb:hover": {
          backgroundColor: dark ? "rgba(255,255,255,0.28)" : "rgba(0,0,0,0.28)",
        },
      },

      // Emoji buttons
      "& .emoji-mart-emoji": {
        borderRadius: "8px",
        padding: "4px",
        transition: "transform 0.12s ease, background-color 0.12s ease",
        "&:hover": {
          backgroundColor: dark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.05)",
          transform: "scale(1.15)",
        },
        "& span": {
          cursor: "pointer",
        },
      },

      // Remove the preview footer
      "& .emoji-mart-preview": {
        display: "none !important",
      },

      [theme.breakpoints.down("sm")]: {
        position: "fixed",
        left: 0,
        right: 0,
        bottom: 56,
        width: "100%",
        maxWidth: "100vw",
        borderRadius: "16px 16px 0 0",
      },
    },

    emojiFooterBar: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 16,
      padding: "8px 16px 10px",
      borderTop: `1px solid ${dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)"}`,
      backgroundColor: dark ? "#111b21" : "#f0f2f5",
    },
    emojiFooterTabActive: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "4px 16px",
      borderRadius: 16,
      backgroundColor: dark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.08)",
      cursor: "pointer",
      color: "#00a884",
    },
    emojiFooterTabInactive: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "4px 16px",
      borderRadius: 16,
      cursor: "pointer",
      color: dark ? "#8696a0" : "#54656f",
      fontWeight: 500,
      fontSize: 12,
      transition: "background-color 0.15s, color 0.15s",
      "&:hover": {
        backgroundColor: dark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)",
        color: dark ? "#e9edef" : "#111b21",
      },
    },

    // ── attachment dropdown ───────────────────────────────────────────────────
    attachMenu: {
      "& .MuiMenu-paper": {
        backgroundColor: dark ? "#233138" : "#fff",
        borderRadius: 14,
        boxShadow: dark
          ? "0 8px 32px rgba(0,0,0,0.55)"
          : "0 4px 20px rgba(0,0,0,0.14)",
        padding: "6px 0",
        minWidth: 190,
        border: dark ? "none" : "1px solid rgba(0,0,0,0.06)",
      },
    },

    attachMenuItem: {
      padding: "10px 16px",
      transition: "background 0.12s",
      "&:hover": {
        backgroundColor: dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)",
      },
      "& .MuiListItemIcon-root": { minWidth: 38 },
    },

    attachIconChip: {
      width: 34,
      height: 34,
      borderRadius: "50%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },

    // ── quick messages autocomplete ───────────────────────────────────────────
    autocompletePaper: {
      backgroundColor: dark ? "#202c33" : "#fff",
      color: dark ? "#e9edef" : "#111b21",
      borderRadius: 12,
      boxShadow: dark
        ? "0 8px 32px rgba(0,0,0,0.6)"
        : "0 4px 20px rgba(0,0,0,0.15)",
      border: `1px solid ${dark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"}`,
      marginBottom: 8,
    },

    autocompleteListbox: {
      padding: "6px 0",
      maxHeight: 280,
      "&::-webkit-scrollbar": { width: 6 },
      "&::-webkit-scrollbar-thumb": {
        backgroundColor: dark ? "rgba(255,255,255,0.16)" : "rgba(0,0,0,0.16)",
        borderRadius: 3,
      },
    },

    autocompleteOption: {
      padding: "8px 16px",
      fontSize: 14,
      transition: "background 0.12s",
      "&[data-focus='true']": {
        backgroundColor: dark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.05)",
      },
      "&:hover": {
        backgroundColor: dark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.05)",
      },
    },

    // ── media preview tray ────────────────────────────────────────────────────
    mediaTray: {
      backgroundColor: bgBar,
      borderTop: `1px solid ${dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.07)"}`,
      padding: "12px 12px 0",
      display: "flex",
      flexDirection: "column",
      gap: 10,
    },

    mediaScroll: {
      display: "flex",
      gap: 10,
      overflowX: "auto",
      paddingBottom: 12,
      "&::-webkit-scrollbar": { height: 4 },
      "&::-webkit-scrollbar-thumb": {
        backgroundColor: dark ? "rgba(255,255,255,0.15)" : "rgba(0,0,0,0.15)",
        borderRadius: 4,
      },
    },

    mediaCard: {
      position: "relative",
      flexShrink: 0,
      width: 90,
      height: 90,
      borderRadius: 10,
      backgroundColor: dark ? "#2a3942" : "#fff",
      border: `1px solid ${dark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"}`,
      overflow: "visible",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: "default",
    },

    mediaCardInner: {
      width: "100%",
      height: "100%",
      borderRadius: 10,
      overflow: "hidden",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: 4,
      padding: "8px 6px 4px",
    },

    mediaThumb: {
      width: "100%",
      height: "100%",
      objectFit: "cover",
      borderRadius: 10,
      display: "block",
    },

    mediaLabel: {
      fontSize: 10,
      color: textMuted,
      textAlign: "center",
      width: "100%",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      lineHeight: 1.3,
    },

    mediaRemoveBtn: {
      position: "absolute",
      top: -8,
      right: -8,
      width: 20,
      height: 20,
      borderRadius: "50%",
      backgroundColor: dark ? "#374045" : "#e0e0e0",
      border: `1.5px solid ${bgBar}`,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: "pointer",
      zIndex: 2,
      transition: "background 0.12s",
      "&:hover": { backgroundColor: dark ? "#4a555c" : "#ccc" },
      "& svg": { fontSize: 12 },
    },

    addMoreBtn: {
      flexShrink: 0,
      width: 90,
      height: 90,
      borderRadius: 10,
      border: `2px dashed ${dark ? "rgba(255,255,255,0.15)" : "rgba(0,0,0,0.12)"}`,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: 4,
      cursor: "pointer",
      color: textMuted,
      fontSize: 11,
      transition: "border-color 0.15s, color 0.15s",
      "&:hover": {
        borderColor: dark ? "rgba(255,255,255,0.3)" : "rgba(0,0,0,0.25)",
        color: textMain,
      },
    },

    // ── media tray actions row ────────────────────────────────────────────────
    mediaActions: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      paddingBottom: 10,
      paddingTop: 2,
    },

    captionInput: {
      flex: 1,
      backgroundColor: bgInput,
      borderRadius: 21,
      padding: "7px 16px",
      fontSize: 14,
      color: textMain,
      "& textarea": { padding: 0 },
      "& ::placeholder": { color: textMuted, opacity: 1 },
    },

    // ── send / audio buttons ──────────────────────────────────────────────────
    circleBtn: {
      width: 42,
      height: 42,
      borderRadius: "50%",
      backgroundColor: "#00a884",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: "pointer",
      flexShrink: 0,
      border: "none",
      padding: 0,
      transition: "background 0.15s, transform 0.1s",
      "&:hover": { backgroundColor: "#06cf9c" },
      "&:active": { transform: "scale(0.93)" },
      "&:disabled": { backgroundColor: dark ? "#374045" : "#d0d0d0", cursor: "not-allowed" },
      "& svg": { color: "#fff", fontSize: 20 },
    },

    ghostBtn: {
      width: 42,
      height: 42,
      borderRadius: "50%",
      background: "transparent",
      border: "none",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: textMuted,
      transition: "background 0.12s, color 0.12s",
      "&:hover": { background: "rgba(0,0,0,0.06)", color: textMain },
      "& svg": { fontSize: 22 },
    },

    // ── recording bar ─────────────────────────────────────────────────────────
    recorderRow: {
      flex: 1,
      display: "flex",
      alignItems: "center",
      gap: 8,
      backgroundColor: bgInput,
      borderRadius: 21,
      padding: "6px 12px",
      minWidth: 0,
    },

    pauseHint: {
      fontSize: 11,
      color: textMuted,
      flexShrink: 0,
      lineHeight: 1,
    },

    // ── upload progress inline ────────────────────────────────────────────────
    uploadingRow: {
      display: "flex",
      alignItems: "center",
      gap: 10,
      padding: "8px 14px",
      borderTop: `1px solid ${dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.07)"}`,
    },

    uploadingLabel: {
      fontSize: 12,
      color: textMuted,
      flexShrink: 0,
    },

    // ── reply banner ──────────────────────────────────────────────────────────
    replyBanner: {
      display: "flex",
      alignItems: "stretch",
      backgroundColor: dark ? "#202c33" : "#f0f2f5",
      borderTop: `1px solid ${dark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.07)"}`,
      padding: "8px 10px 8px 16px",
      gap: 10,
    },

    replyAccent: {
      width: 3,
      borderRadius: 2,
      backgroundColor: "#00a884",
      flexShrink: 0,
    },

    replyBody: {
      flex: 1,
      minWidth: 0,
      fontSize: 13,
      color: textMuted,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },

    replyName: {
      fontSize: 12,
      fontWeight: 600,
      color: "#00a884",
      marginBottom: 2,
    },
  };
});

// ─── sub-components ───────────────────────────────────────────────────────────

const EmojiOptions = ({ disabled, showEmoji, setShowEmoji, handleAddEmoji }) => {
  const classes = useStyles();
  const { palette } = useTheme();
  const dark = palette.type === "dark";
  const emojiBoxRef = useRef(null);

  useEffect(() => {
    if (!showEmoji) return;
    const handleClickOutside = (event) => {
      if (emojiBoxRef.current && !emojiBoxRef.current.contains(event.target)) {
        setShowEmoji(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showEmoji, setShowEmoji]);

  return (
    <div ref={emojiBoxRef} style={{ position: "relative" }}>
      <Tooltip title="Emoji" placement="top">
        <span>
          <IconButton size="small" disabled={disabled} onClick={() => setShowEmoji(v => !v)}>
            <MoodIcon className={classes.sendMessageIcons} style={{ color: showEmoji ? "#00a884" : undefined }} />
          </IconButton>
        </span>
      </Tooltip>
      {showEmoji && (
        <div className={classes.emojiBox}>
          <Picker
            perLine={9}
            showPreview={false}
            showSkinTones={false}
            onSelect={handleAddEmoji}
            native
            color="#00a884"
            emojiSize={26}
            title=""
            theme={dark ? "dark" : "light"}
            i18n={emojiI18n}
          />
          <div className={classes.emojiFooterBar}>
            <div className={classes.emojiFooterTabActive} title="Emojis">
              <MoodIcon style={{ fontSize: 20, color: "#00a884" }} />
            </div>
            <div className={classes.emojiFooterTabInactive} title="GIFs">
              GIF
            </div>
            <div className={classes.emojiFooterTabInactive} title="Figurinhas">
              <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
                <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h9l7-7V5c0-1.1-.9-2-2-2zm-5 15.5V14h4.5L14 18.5zM19 12h-6c-.55 0-1 .45-1 1v6H5V5h14v7z" />
              </svg>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const SignSwitch = ({ width, signMessage, setSignMessage }) => {
  if (!isWidthUp("md", width)) return null;
  return (
    <FormControlLabel
      style={{ marginRight: 4, color: "gray", whiteSpace: "nowrap" }}
      label={i18n.t("messagesInput.signMessage")}
      labelPlacement="start"
      control={
        <Switch size="small" checked={signMessage}
          onChange={e => setSignMessage(e.target.checked)}
          name="signMessage" color="primary" />
      }
    />
  );
};

const ATTACH_ITEMS = [
  { label: "Documento",      accept: "*",             icon: InsertDriveFileOutlinedIcon, color: "#7f66ff", bg: "rgba(127,102,255,0.12)" },
  { label: "Fotos e vídeos", accept: "image/*,video/*", icon: ImageOutlinedIcon,          color: "#007bfc", bg: "rgba(0,123,252,0.12)" },
  { label: "Áudio",          accept: "audio/*",        icon: HeadsetOutlinedIcon,         color: "#ee6e00", bg: "rgba(238,110,0,0.12)" },
];

const AttachmentMenu = ({ handleChangeMedias, disableOption }) => {
  const classes = useStyles();
  const [anchor, setAnchor] = useState(null);
  const refs = [useRef(), useRef(), useRef()];

  return (
    <>
      {refs.map((r, i) => (
        <input key={i} multiple type="file" ref={r}
          accept={ATTACH_ITEMS[i].accept}
          className={classes.uploadInput}
          onChange={handleChangeMedias} />
      ))}
      <Tooltip title="Anexar" placement="top">
        <span>
          <IconButton size="small" disabled={disableOption()} onClick={e => setAnchor(e.currentTarget)}>
            <AttachFileIcon className={classes.sendMessageIcons} />
          </IconButton>
        </span>
      </Tooltip>
      <Menu
        anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}
        className={classes.attachMenu}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        transformOrigin={{ vertical: "bottom", horizontal: "center" }}
        getContentAnchorEl={null}
        TransitionComponent={Fade}
      >
        {ATTACH_ITEMS.map(({ label, icon: Icon, color, bg }, i) => (
          <MenuItem key={label} className={classes.attachMenuItem}
            onClick={() => {
              setAnchor(null);
              if (refs[i].current) {
                refs[i].current.value = null;
                refs[i].current.click();
              }
            }}>
            <ListItemIcon>
              <div className={classes.attachIconChip} style={{ background: bg }}>
                <Icon style={{ color, fontSize: 20 }} />
              </div>
            </ListItemIcon>
            <ListItemText
              primary={label}
              primaryTypographyProps={{ style: { fontSize: 14 } }}
            />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

const ActionButtons = ({
  inputMessage, loading, recording, ticketStatus,
  handleSendMessage, handleCancelAudio, handleUploadAudio,
  handleStartRecording, handleOpenModalForward, showSelectMessageCheckbox,
}) => {
  const classes = useStyles();

  if (inputMessage || showSelectMessageCheckbox) {
    return (
      <IconButton
        className={classes.circleBtn}
        onClick={showSelectMessageCheckbox ? handleOpenModalForward : handleSendMessage}
        disabled={loading}
        aria-label={showSelectMessageCheckbox ? "Encaminhar" : "Enviar"}
      >
        {showSelectMessageCheckbox
          ? <Reply style={{ transform: "scaleX(-1)" }} />
          : <SendIcon />}
      </IconButton>
    );
  }

  if (recording) {
    return (
      <>
        <IconButton onClick={handleCancelAudio} disabled={loading} aria-label="Descartar gravação"
          style={{ color: "#8696a0" }}>
          <DeleteOutlineIcon />
        </IconButton>
        {loading
          ? <CircularProgress size={24} style={{ color: "#00a884", margin: "0 10px" }} />
          : null}
        <IconButton className={classes.circleBtn} onClick={handleUploadAudio} disabled={loading} aria-label="Enviar áudio">
          <SendIcon />
        </IconButton>
      </>
    );
  }

  return (
    <IconButton
      onClick={handleStartRecording}
      disabled={loading || ticketStatus !== "open"}
      aria-label="Gravar áudio"
      style={{ color: loading || ticketStatus !== "open" ? "rgba(134,150,160,0.4)" : "#54656f" }}
    >
      <MicIcon />
    </IconButton>
  );
};

const CustomInput = ({
  loading, inputRef, ticketStatus, inputMessage, setInputMessage,
  handleSendMessage, handleInputPaste, disableOption, handleQuickAnswersClick,
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const dark = theme.palette.type === "dark";
  const history = useHistory();
  const [quickMessages, setQuickMessages] = useState([]);
  const [options, setOptions] = useState([]);
  const [popupOpen, setPopupOpen] = useState(false);
  const { user } = useContext(AuthContext);
  const { list: listQuickMessages } = useQuickMessages();

  const normalize = (str) =>
    (str || "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase();

  useEffect(() => {
    let isMounted = true;
    (async () => {
      try {
        const companyId = localStorage.getItem("companyId");
        const messages = await listQuickMessages({ companyId, userId: user?.id });
        if (isMounted && Array.isArray(messages)) {
          setQuickMessages(messages.map(m => ({
            shortcode: m.shortcode,
            value: m.message,
            label: `/${m.shortcode} - ${m.message}`,
            mediaPath: m.mediaPath,
          })));
        }
      } catch (err) {
        console.error("Failed to load quick messages", err);
      }
    })();
    return () => { isMounted = false; };
  }, [user?.id]);

  useEffect(() => {
    if (isString(inputMessage) && inputMessage.startsWith("/")) {
      const query = normalize(inputMessage.slice(1).trim());
      if (!query) {
        setOptions(quickMessages);
        setPopupOpen(quickMessages.length > 0);
      } else {
        const filtered = quickMessages.filter(m => {
          const normLabel = normalize(m.label);
          const normShort = normalize(m.shortcode);
          const normVal = normalize(m.value);
          return normLabel.includes(query) || normShort.includes(query) || normVal.includes(query);
        });
        setOptions(filtered);
        setPopupOpen(filtered.length > 0);
      }
    } else {
      setPopupOpen(false);
    }
  }, [inputMessage, quickMessages]);

  return (
    <div className={classes.messageInputWrapper}>
      <Autocomplete
        freeSolo
        autoHighlight
        filterSelectedOptions={false}
        filterOptions={(x) => x}
        open={popupOpen}
        onClose={() => setPopupOpen(false)}
        value={inputMessage}
        options={options}
        closeIcon={null}
        classes={{
          paper: classes.autocompletePaper,
          listbox: classes.autocompleteListbox,
          option: classes.autocompleteOption,
        }}
        getOptionLabel={opt => isObject(opt) ? opt.label : opt}
        renderOption={opt => (
          <div style={{ display: "flex", alignItems: "center", width: "100%", gap: 8, overflow: "hidden" }}>
            <span style={{ fontWeight: 600, color: dark ? "#00a884" : "#008069", flexShrink: 0 }}>
              /{opt.shortcode}
            </span>
            <span style={{ color: dark ? "#8696a0" : "#54656f", flexShrink: 0 }}>-</span>
            <span style={{
              color: dark ? "#e9edef" : "#111b21",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              flex: 1,
            }}>
              {opt.value}
            </span>
            {opt.mediaPath && (
              <AttachFileIcon style={{ fontSize: 16, color: dark ? "#8696a0" : "#54656f", flexShrink: 0 }} />
            )}
          </div>
        )}
        onChange={(_, opt) => {
          if (isObject(opt) && has(opt, "value")) {
            if (isNil(opt.mediaPath)) {
              setInputMessage(opt.value);
            } else {
              handleQuickAnswersClick(opt);
            }
            setPopupOpen(false);
            setTimeout(() => {
              if (inputRef.current) {
                inputRef.current.focus();
                inputRef.current.scrollTop = inputRef.current.scrollHeight;
              }
            }, 50);
          }
        }}
        onInputChange={(_, opt, reason) => {
          if (reason === "input") setInputMessage(opt);
        }}
        onPaste={e => ticketStatus === "open" && handleInputPaste(e)}
        onKeyPress={e => {
          if (!loading && !e.shiftKey && e.key === "Enter" && !popupOpen) {
            handleSendMessage();
          }
        }}
        onKeyDown={e => {
          if ((e.key === "ArrowUp" || e.key === "ArrowDown") && !popupOpen) {
            e.stopPropagation();
          }
        }}
        style={{ width: "100%" }}
        renderInput={params => {
          const { InputLabelProps, InputProps, ...rest } = params;
          return (
            <InputBase
              {...params.InputProps} {...rest}
              disabled={disableOption()}
              inputRef={input => { if (input) { inputRef.current = input; } }}
              placeholder={ticketStatus === "open"
                ? i18n.t("messagesInput.placeholderOpen")
                : i18n.t("messagesInput.placeholderClosed")}
              multiline maxRows={5}
              className={classes.messageInput}
              onKeyDown={e => {
                if (e.key === "ArrowUp" || e.key === "ArrowDown") {
                  if (!popupOpen) {
                    e.stopPropagation();
                  }
                } else if (e.key === "Escape") {
                  if (popupOpen) {
                    setPopupOpen(false);
                  } else {
                    history.push("/tickets");
                  }
                }
              }}
            />
          );
        }}
      />
    </div>
  );
};

// ─── media preview card ───────────────────────────────────────────────────────

const MediaCard = ({ file, onRemove }) => {
  const classes = useStyles();
  const [thumb, setThumb] = useState(null);

  useEffect(() => {
    const url = getMediaThumb(file);
    if (url) { setThumb(url); return () => URL.revokeObjectURL(url); }
  }, [file]);

  return (
    <div className={classes.mediaCard}>
      <div className={classes.mediaCardInner}>
        {thumb
          ? <img src={thumb} alt={file.name} className={classes.mediaThumb} />
          : (
            <>
              {getMediaIcon(file)}
              <span className={classes.mediaLabel}>{file.name}</span>
            </>
          )}
      </div>
      <button className={classes.mediaRemoveBtn} onClick={onRemove} aria-label="Remover">
        <ClearIcon />
      </button>
    </div>
  );
};

// ─── reply banner ─────────────────────────────────────────────────────────────

const ReplyBanner = ({ message, onClose }) => {
  const classes = useStyles();
  return (
    <div className={classes.replyBanner}>
      <div className={classes.replyAccent} />
      <div style={{ flex: 1, minWidth: 0 }}>
        {!message.fromMe && (
          <div className={classes.replyName}>{message.contact?.name}</div>
        )}
        <div className={classes.replyBody}>{message.body}</div>
      </div>
      <IconButton size="small" onClick={onClose} aria-label="Fechar resposta">
        <ClearIcon style={{ fontSize: 18, color: "#8696a0" }} />
      </IconButton>
    </div>
  );
};

// ─── main component ───────────────────────────────────────────────────────────

const MessageInputCustom = (props) => {
  const { ticketStatus, ticketId } = props;
  const classes = useStyles();

  const [percentLoading, setPercentLoading] = useState(0);
  const [medias, setMedias] = useState([]);
  const [caption, setCaption] = useState("");
  const [inputMessage, setInputMessage] = useState("");
  const [showEmoji, setShowEmoji] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingSuggestion, setLoadingSuggestion] = useState(false);
  const [recording, setRecording] = useState(false);
  const [audioStream, setAudioStream] = useState(null);
  const [showSuggestionButton, setShowSuggestionButton] = useState(false);
  const inputRef = useRef();
  const addMoreRef = useRef();
  const captionInputRef = useRef();

  const { setReplyingMessage, replyingMessage } = useContext(ReplyMessageContext);
  const { user } = useContext(AuthContext);
  const { getPlanCompany } = usePlans();
  const [signMessage, setSignMessage] = useLocalStorage("signOption", true);
  const { selectedMessages, setSelectedMessages, setForwardMessageModalOpen, showSelectMessageCheckbox, setShowSelectMessageCheckbox } = useContext(ForwardMessageContext);

  const handleCloseForwardSelection = () => {
    setShowSelectMessageCheckbox(false);
    setSelectedMessages([]);
  };

  // focus when reply changes
  useEffect(() => { if (inputRef.current) inputRef.current.focus(); }, [replyingMessage]);

  // Limpar drafts corrompidos ou indefinidos
  try {
    localStorage.removeItem("draft_undefined");
    localStorage.removeItem("draft_null");
  } catch (_) {}

  const isValidTicketId = Boolean(ticketId && ticketId !== "undefined" && String(ticketId).trim() !== "");

  // restore draft on ticket change
  useEffect(() => {
    if (inputRef.current) inputRef.current.focus();
    if (!isValidTicketId) {
      setInputMessage("");
      return;
    }
    const draft = localStorage.getItem(`draft_${ticketId}`);
    setInputMessage(draft || "");
    return () => { setShowEmoji(false); setMedias([]); setCaption(""); setReplyingMessage(null); };
  }, [ticketId, isValidTicketId, setReplyingMessage]);

  // persist draft
  useEffect(() => {
    if (!isValidTicketId) return;
    if (inputMessage.trim()) {
      localStorage.setItem(`draft_${ticketId}`, inputMessage);
      window.dispatchEvent(new Event("draftUpdate"));
    } else {
      localStorage.removeItem(`draft_${ticketId}`);
      window.dispatchEvent(new Event("draftUpdate"));
    }
  }, [inputMessage, ticketId, isValidTicketId]);

  // AI suggestion button
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!user?.companyId) return;
      try {
        const [planConfigs, settingsRes] = await Promise.all([
          getPlanCompany(undefined, user.companyId),
          api.get("/settings"),
        ]);
        const ai = Array.isArray(settingsRes.data)
          ? settingsRes.data.find(s => s.key === "aiAgentEnabled")
          : null;
        if (!cancelled) setShowSuggestionButton(Boolean(planConfigs?.plan?.useOpenAi) && ai?.value === "enabled");
      } catch { if (!cancelled) setShowSuggestionButton(false); }
    })();
    return () => { cancelled = true; };
  }, [getPlanCompany, user?.companyId]);

  const disableOption = () => loading || recording || ticketStatus !== "open";

  const handleAddEmoji = e => setInputMessage(p => p + e.native);

  // ── drag & drop ─────────────────────────────────────────────────────────
  const [dragOver, setDragOver] = useState(false);

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (ticketStatus === "open") setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    if (ticketStatus !== "open") return;
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const newFiles = Array.from(e.dataTransfer.files);
      if (inputMessage.trim() && !caption) {
        setCaption(inputMessage.trim());
      }
      setMedias(prev => [...prev, ...newFiles]);
      e.dataTransfer.clearData();
      setTimeout(() => {
        if (captionInputRef.current) {
          captionInputRef.current.focus();
        }
      }, 50);
    }
  };

  const handleChangeMedias = e => {
    if (!e.target.files || e.target.files.length === 0) return;
    const newFiles = Array.from(e.target.files);
    if (inputMessage.trim() && !caption) {
      setCaption(inputMessage.trim());
    }
    setMedias(prev => [...prev, ...newFiles]);
    e.target.value = null;
    setTimeout(() => {
      if (captionInputRef.current) {
        captionInputRef.current.focus();
      }
    }, 50);
  };

  const handleInputPaste = e => {
    let files = [];
    if (e.clipboardData?.items) {
      for (const item of Array.from(e.clipboardData.items)) {
        if (item.kind === "file") {
          const file = item.getAsFile();
          if (file) files.push(file);
        }
      }
    }
    if (files.length === 0 && e.clipboardData?.files) {
      files = Array.from(e.clipboardData.files);
    }
    if (files.length > 0) {
      e.preventDefault();
      if (inputMessage.trim() && !caption) {
        setCaption(inputMessage.trim());
      }
      setMedias(prev => [...prev, ...files]);
      setTimeout(() => {
        if (captionInputRef.current) {
          captionInputRef.current.focus();
        }
      }, 50);
    }
  };

  const handleSendMessage = async () => {
    if (inputMessage.trim() === "") return;
    setLoading(true);
    try {
      await api.post(`/messages/${ticketId}`, {
        read: 1, fromMe: true, mediaUrl: "",
        body: signMessage ? `*${user?.name}:*\n${inputMessage.trim()}` : inputMessage.trim(),
        quotedMsg: replyingMessage,
      });
      localStorage.removeItem(`draft_${ticketId}`);
      window.dispatchEvent(new Event("draftUpdate"));
    } catch (err) { toastError(err); }
    setInputMessage(""); setShowEmoji(false); setLoading(false); setReplyingMessage(null);
  };

  const handleStartRecording = async () => {
    setLoading(true);
    try {
      await Mp3Recorder.start();
      let stream = Mp3Recorder.activeStream || Mp3Recorder.stream;
      if (!stream) {
        for (const k of Object.keys(Mp3Recorder)) {
          if (Mp3Recorder[k] && typeof Mp3Recorder[k].getAudioTracks === "function") {
            stream = Mp3Recorder[k];
            break;
          }
        }
      }
      setAudioStream(stream);
      setRecording(true);
    } catch (err) { toastError(err); }
    setLoading(false);
  };

  const handleUploadAudio = async () => {
    setLoading(true);
    try {
      const [, blob] = await Mp3Recorder.stop().getMp3();
      setAudioStream(null);
      if (blob.size < 10000) { setLoading(false); setRecording(false); return; }
      const formData = new FormData();
      const filename = `audio-record-site-${Date.now()}.mp3`;
      formData.append("medias", blob, filename);
      formData.append("body", filename);
      formData.append("fromMe", true);
      await api.post(`/messages/${ticketId}`, formData);
    } catch (err) { toastError(err); }
    setAudioStream(null);
    setRecording(false); setLoading(false);
  };

  const handleCancelAudio = async () => {
    try { await Mp3Recorder.stop().getMp3(); } catch (err) { toastError(err); }
    setAudioStream(null);
    setRecording(false);
  };

  const handleOpenModalForward = () => {
    if (!selectedMessages.length) {
      setForwardMessageModalOpen(false);
      toastError(i18n.t("messagesList.header.notMessage"));
      return;
    }
    setForwardMessageModalOpen(true);
  };

  const handleUploadQuickMessageMedia = async (blob, message, mediaPath) => {
    setLoading(true);
    try {
      let fileName = "";
      if (mediaPath) {
        const parts = mediaPath.split("/");
        fileName = parts[parts.length - 1] || "";
      }
      if (!fileName || fileName.startsWith("blob")) {
        const ext = blob.type ? blob.type.split("/")[1] : "bin";
        fileName = `midia_${Date.now()}.${ext}`;
      }
      fileName = fileName.replace(/^\d+[-_]/, "");
      const fd = new FormData();
      fd.append("medias", blob, fileName);
      fd.append("body", message || fileName);
      fd.append("fromMe", true);
      await api.post(`/messages/${ticketId}`, fd);
    } catch (err) { toastError(err); }
    setLoading(false);
  };

  const handleQuickAnswersClick = async (value) => {
    if (value.mediaPath) {
      try {
        const { data } = await axios.get(value.mediaPath, { responseType: "blob" });
        handleUploadQuickMessageMedia(data, value.value, value.mediaPath);
      } catch (err) { toastError(err); }
        setInputMessage(""); return;
    }
    setInputMessage(value.value);
  };

  const handleUploadMedia = async () => {
    setLoading(true);
    const formData = new FormData();
    formData.append("fromMe", true);
    const finalCaption = caption.trim() || inputMessage.trim();
    if (finalCaption) formData.append("body", finalCaption);

    await Promise.all(medias.map(media =>
      new Promise(resolve => {
        if (media.type?.startsWith("image/")) {
          new Compressor(media, {
            quality: 0.7,
            success(compressed) { formData.append("medias", compressed, media.name); resolve(); },
            error() { formData.append("medias", media, media.name); resolve(); },
          });
        } else {
          formData.append("medias", media, media.name);
          if (!finalCaption) formData.append("body", media.name);
          resolve();
        }
      })
    ));

    setTimeout(async () => {
      try {
        await api.post(`/messages/${ticketId}`, formData, {
          onUploadProgress: e => setPercentLoading(Math.round((e.loaded * 100) / e.total)),
        });
        setMedias([]); setCaption(""); setInputMessage(""); setPercentLoading(0);
        localStorage.removeItem(`draft_${ticketId}`);
        window.dispatchEvent(new Event("draftUpdate"));
        setTimeout(() => inputRef.current?.focus(), 50);
      } catch (err) { toastError(err); }
      setLoading(false);
    }, 600);
  };

  const handleGetSuggestion = async () => {
    setLoadingSuggestion(true);
    try {
      const { data } = await api.post(`/messages/${ticketId}/suggest`);
      const suggestion = data?.suggestion?.trim?.() || "";
      if (!suggestion || [
        "Nenhuma configuração", "Nenhuma conversa", "Nenhuma chave",
        "A chave de API", "O modelo configurado", "O provedor de IA",
      ].some(s => suggestion.includes(s))) {
        toast.warn(suggestion || "Não foi possível gerar sugestão agora.");
        return;
      }
      setInputMessage(suggestion);
    } catch (err) { toastError(err); }
    setLoadingSuggestion(false);
  };

  // ── media tray ──────────────────────────────────────────────────────────────
  if (medias.length > 0) {
    return (
      <Paper elevation={0} square className={classes.mediaTray}
        onPaste={handleInputPaste}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {/* scrollable file chips */}
        <div className={classes.mediaScroll}>
          {medias.map((media, i) => (
            <MediaCard key={i} file={media} onRemove={() => setMedias(p => p.filter((_, j) => j !== i))} />
          ))}
          {/* add more button */}
          <input multiple type="file" ref={addMoreRef} className={classes.uploadInput} onChange={handleChangeMedias} />
          <div className={classes.addMoreBtn} onClick={() => {
            if (addMoreRef.current) {
              addMoreRef.current.value = null;
              addMoreRef.current.click();
            }
          }} role="button" tabIndex={0}
            onKeyPress={e => {
              if (e.key === "Enter" && addMoreRef.current) {
                addMoreRef.current.value = null;
                addMoreRef.current.click();
              }
            }}>
            <span style={{ fontSize: 22, lineHeight: 1 }}>+</span>
            <span>Adicionar</span>
          </div>
        </div>

        {/* upload progress — only visible while uploading */}
        {loading && (
          <div className={classes.uploadingRow}>
            <span className={classes.uploadingLabel}>Enviando…</span>
            <ProgressBarCustom progress={percentLoading} />
          </div>
        )}

        {/* caption + send row */}
        <div className={classes.mediaActions}>
          <IconButton size="small" onClick={() => { setMedias([]); setCaption(""); setTimeout(() => inputRef.current?.focus(), 50); }} disabled={loading} aria-label="Cancelar">
            <CancelIcon style={{ color: "#8696a0" }} />
          </IconButton>
          <div className={classes.captionInput} style={{ flex: 1 }}>
            <InputBase
              fullWidth multiline maxRows={3}
              inputRef={input => {
                captionInputRef.current = input;
                if (input) input.focus();
              }}
              autoFocus
              disabled={loading}
              placeholder="Adicione uma legenda…"
              value={caption}
              onChange={e => setCaption(e.target.value)}
              onPaste={handleInputPaste}
              onKeyDown={e => {
                if (e.key === "Escape") {
                  e.preventDefault();
                  setMedias([]);
                  setCaption("");
                  setTimeout(() => inputRef.current?.focus(), 50);
                } else if (!e.shiftKey && e.key === "Enter") {
                  e.preventDefault();
                  handleUploadMedia();
                }
              }}
              style={{ fontSize: 14, width: "100%" }}
            />
          </div>
          <IconButton className={classes.circleBtn} onClick={handleUploadMedia} disabled={loading} aria-label="Enviar arquivos">
            <SendIcon />
          </IconButton>
        </div>
      </Paper>
    );
  }

  // ── main bar ────────────────────────────────────────────────────────────────
  return (
    <Paper square elevation={0} className={classes.mainWrapper}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      style={dragOver ? { outline: "2px dashed #00a884", outlineOffset: "-2px" } : undefined}
    >
      {replyingMessage && !showSelectMessageCheckbox && (
        <ReplyBanner message={replyingMessage} onClose={() => setReplyingMessage(null)} />
      )}

      {showSelectMessageCheckbox ? (
        <div className={classes.selectionBar}>
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <IconButton size="small" onClick={handleCloseForwardSelection} aria-label="Cancelar seleção">
              <ClearIcon style={{ color: "#8696a0" }} />
            </IconButton>
            <Typography style={{ fontSize: 16, color: classes.textMain }}>
              {selectedMessages.length} itens selecionados
            </Typography>
          </div>
          <IconButton
            className={classes.ghostBtn}
            disabled={selectedMessages.length === 0}
            onClick={handleOpenModalForward}
            aria-label="Encaminhar mensagens"
          >
            <Reply style={{ transform: "scaleX(-1)", color: selectedMessages.length === 0 ? "#8696a0" : "#00a884" }} />
          </IconButton>
        </div>
      ) : (
        <div className={classes.newMessageBox}>
          {/* AI suggestion */}
          {showSuggestionButton && !recording && (
            <Tooltip title="Sugerir resposta" placement="top">
              <span>
                <IconButton size="small" onClick={handleGetSuggestion} disabled={loadingSuggestion || disableOption()}>
                  {loadingSuggestion
                    ? <CircularProgress size={20} style={{ color: "#00a884" }} />
                    : <WbIncandescentIcon className={classes.sendMessageIcons} />}
                </IconButton>
              </span>
            </Tooltip>
          )}

          {/* emoji */}
          {!recording && (
            <EmojiOptions disabled={disableOption()} handleAddEmoji={handleAddEmoji}
              showEmoji={showEmoji} setShowEmoji={setShowEmoji} />
          )}

          {/* attach */}
          {!recording && (
            <AttachmentMenu disableOption={disableOption} handleChangeMedias={handleChangeMedias} />
          )}

          {/* sign switch */}
          {!recording && (
            <SignSwitch width={props.width} signMessage={signMessage} setSignMessage={setSignMessage} />
          )}

          {/* text input OR recording UI */}
          {recording ? (
            <div className={classes.recorderRow}>
              <RecordingTimer audioStream={audioStream} />
            </div>
          ) : (
            <CustomInput
              loading={loading} inputRef={inputRef} ticketStatus={ticketStatus}
              inputMessage={inputMessage} setInputMessage={setInputMessage}
              handleSendMessage={handleSendMessage} handleInputPaste={handleInputPaste}
              disableOption={disableOption} handleQuickAnswersClick={handleQuickAnswersClick}
            />
          )}

          {/* action buttons */}
          <ActionButtons
            inputMessage={inputMessage} loading={loading} recording={recording}
            ticketStatus={ticketStatus} handleSendMessage={handleSendMessage}
            handleCancelAudio={handleCancelAudio} handleUploadAudio={handleUploadAudio}
            handleStartRecording={handleStartRecording}
            handleOpenModalForward={handleOpenModalForward}
            showSelectMessageCheckbox={false} // Force false since we have the dedicated bar now
          />
        </div>
      )}
    </Paper>
  );
};

export default withWidth()(MessageInputCustom);
